import {
  ErrorBoundary as InternalRollbarErrorBoundary,
  RollbarContext,
  Provider as RollbarProvider,
} from '@rollbar/react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';

import ErrorPage from '../Pages/Error';

import { isRollbarEnabled, logLocal } from './api';
import { getRollbar } from './config';

const localHistoryListener = (location) => {
  logLocal(`History Context Change (Rollbar): ${location.pathname}`, {
    location,
  });
};

/**
 * Component that automatically sets Rollbar context based on current route
 * This is the modern way to handle route context with @rollbar/react 1.0.0
 */
const RollbarRouteContext = ({ children }) => {
  const location = useLocation();

  // Create context string from current location
  const context = location.pathname + location.search;

  if (isRollbarEnabled()) {
    // Log context changes for debugging
    logLocal(`Rollbar Context Updated: ${context}`, { location });

    return <RollbarContext context={context}>{children}</RollbarContext>;
  }

  // In development/testing, just log the context change
  localHistoryListener(location);
  return children;
};

const localErrorListener = (error, info) => {
  logLocal('Local Error Boundary (Rollbar):', { error, info });
};
export const LocalErrorBoundary = ({ children, fallback = ErrorPage, errorListener = localErrorListener }) => {
  return (
    <ReactErrorBoundary FallbackComponent={fallback} onError={errorListener}>
      {children}
    </ReactErrorBoundary>
  );
};
const RollbarErrorBoundary = ({ children, fallback = ErrorPage }) => {
  return <InternalRollbarErrorBoundary fallbackUI={fallback}>{children}</InternalRollbarErrorBoundary>;
};

/**
 * Makes the Rollbar context available with proper React Router v7 integration
 */
export const RollbarLoggingProvider = ({ children }) => {
  const rollbar = getRollbar();

  if (isRollbarEnabled()) {
    return (
      <RollbarProvider instance={rollbar}>
        <RollbarErrorBoundary>
          <RollbarRouteContext>{children}</RollbarRouteContext>
        </RollbarErrorBoundary>
      </RollbarProvider>
    );
  } else {
    return (
      <LocalErrorBoundary>
        <RollbarRouteContext>{children}</RollbarRouteContext>
      </LocalErrorBoundary>
    );
  }
};

/**
 * Track user information in Rollbar without using hooks
 * @param {Object} user - The current user object
 *
 * Note: This still uses rollbar.configure() for person tracking as it's
 * a one-time setup that should persist globally, unlike route context
 * which changes frequently.
 */
export const trackRollbarUser = (user, organizationId) => {
  if (!user || !isRollbarEnabled()) return;

  const rollbar = getRollbar();
  if (rollbar) {
    rollbar.configure({
      payload: {
        person: {
          id: user.uid,
          email: user.email,
          displayName: user.displayName,
          creationTime: user.creationTime,
          organizationId,
        },
      },
    });

    logLocal('Rollbar User Tracking Updated', {
      userId: user.uid,
      organizationId,
    });
  }
};
